import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Button from 'components/partials/button/button';
import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';

import { formatDate } from 'helpers/dates-formatter';

import {
  AID_MODEL_STATUS_LABELS,
  AID_MODEL_STATUS_STYLES,
} from './aid-studio.constants';

import Icons from 'assets/icons';

const commonStyles = 'border-r-2 border-white font-semibold cursor-pointer';

interface StatusBadgeProps {
  status: string;
}

const StatusBadge = ({ status }: StatusBadgeProps): React.JSX.Element => {
  const label =
    (AID_MODEL_STATUS_LABELS as Record<string, string>)[status] || status;
  const styles =
    (AID_MODEL_STATUS_STYLES as Record<string, string>)[status] ||
    'bg-gray-200 text-gray-700';

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles}`}
    >
      {label}
    </span>
  );
};

interface CellInfo {
  renderValue: () => unknown;
  getValue: () => unknown;
  row: { original: { id: string | number } };
}

export const getAidStudioTableSettings = (
  clientId: string | number
): unknown[] => [
  {
    id: 'name',
    accessorKey: 'name',
    header: () => (
      <div className="flex">
        <span>MODEL NAME</span>
      </div>
    ),
    cell: (info: CellInfo) => (
      <TruncatedCell value={info.renderValue() as string} />
    ),
    headerClassName: `${commonStyles} rounded-tl-md rounded-bl-md`,
    className: 'w-[20%] text-left font-semibold pl-4 max-w-0',
    enableSorting: true,
    sortingFn: 'alphanumeric',
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: () => <span>STATUS</span>,
    cell: (info: CellInfo) => <StatusBadge status={info.getValue() as string} />,
    headerClassName: `${commonStyles} px-4`,
    className: 'w-[10%] text-center',
    isHeaderCentered: true,
    enableSorting: true,
  },
  {
    id: 'entryTerm',
    accessorKey: 'entryTerm',
    header: () => <span>ENTRY TERM</span>,
    cell: (info: CellInfo) => info.renderValue() as React.ReactNode,
    headerClassName: `${commonStyles} px-4`,
    className: 'w-[10%] text-center',
    isHeaderCentered: true,
    enableSorting: true,
  },
  {
    id: 'createdDate',
    accessorKey: 'createdDate',
    header: () => <span>UPDATED</span>,
    cell: (info: CellInfo) =>
      formatDate(info.getValue() as string, 'DD.MM.YYYY'),
    headerClassName: commonStyles,
    className: 'w-[12%] text-center',
    isHeaderCentered: true,
    enableSorting: true,
  },
  {
    id: 'createdBy',
    accessorKey: 'createdBy',
    header: () => <span>AUTHOR</span>,
    cell: (info: CellInfo) => (
      <TruncatedCell value={info.renderValue() as string} />
    ),
    headerClassName: commonStyles,
    className: 'w-[15%] text-left pl-4 max-w-0',
    enableSorting: true,
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: () => <span>DESCRIPTION</span>,
    cell: (info: CellInfo) => (
      <TruncatedCell value={info.renderValue() as string} />
    ),
    headerClassName: commonStyles,
    className: 'w-[18%] text-left pl-4 max-w-0',
    enableSorting: false,
  },
  {
    id: 'button-cell',
    cell: (info: CellInfo) => (
      <RouterLink
        to={`/admin/clients/${clientId}/aid-studio/${info.row.original.id}`}
        data-testid="view-aid-model-button"
      >
        <Button
          variant="reserve-outlined"
          size="sm"
          iconBefore={<Icons.Eye aria-label="EyeIcon" />}
          className="text-xs md:text-sm"
        >
          View Details
        </Button>
      </RouterLink>
    ),
    headerClassName: 'h-12 rounded-tr-md rounded-br-md',
    className: 'flex justify-center min-w-[7rem] py-3',
  },
];
